import { createRoute, redirect } from '@tanstack/react-router'

import { appRoute } from '@/app/routes/app-route'
import { rootRoute } from '@/app/routes/root'
import { lazyPage } from '@/lib/lazy-page'
import { isLocalRedirect } from '@/lib/oidc'
import { userManagementScopeRoute } from '@/modules/iam'

import {
  interactiveLoginApi,
  interactiveLoginErrorMessage,
} from './api/interactive-login-api'

export type LoginStep = 'identify' | 'password'
export type LoginError =
  | 'configuration'
  | 'expired'
  | 'external'
  | 'invalid'
  | 'policy'
  | 'provider'
  | 'unavailable'

export const AuthLogin = lazyPage(() => import('./features/auth-login/auth-login.lazy'), 'AuthLogin')

export const authLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/login',
  validateSearch: (
    search: Record<string, unknown>,
  ): { error?: LoginError; step?: LoginStep; redirect?: string } => ({
    error:
      search.error === 'configuration' ||
      search.error === 'expired' ||
      search.error === 'external' ||
      search.error === 'invalid' ||
      search.error === 'policy' ||
      search.error === 'provider' ||
      search.error === 'unavailable'
        ? search.error
        : undefined,
    step:
      search.step === 'identify' || search.step === 'password'
        ? search.step
        : undefined,
    redirect: isLocalRedirect(search.redirect) ? search.redirect : undefined,
  }),
  loaderDeps: ({ search }) => ({ step: search.step }),
  loader: async ({ deps }) => {
    if (deps.step !== 'password') return null
    try {
      const { loginHint } = await interactiveLoginApi.readFlow()
      return { loginHint, error: null }
    } catch (cause) {
      return {
        loginHint: null,
        error: interactiveLoginErrorMessage(cause),
      }
    }
  },
  beforeLoad: ({ context, search }) => {
    // Test SSO starts a separate flow in a popup while the main portal session stays signed in.
    // A server-selected step must finish that flow instead of sending the popup back to the portal.
    if (context.isAuthenticated && !search.step && !search.error)
      throw redirect({ to: '/' })
  },
  component: AuthLogin,
})

export const AuthCallback = lazyPage(
  () => import('./features/auth-callback/auth-callback.lazy'),
  'AuthCallback',
)

export const authCallbackRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/callback',
  component: AuthCallback,
})

export const AuthReset = lazyPage(() => import('./features/auth-reset/auth-reset.lazy'), 'AuthReset')

export const authResetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/reset',
  component: AuthReset,
})

export const Signup = lazyPage(() => import('./features/signup/signup.lazy'), 'Signup')

export const signupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/signup',
  component: Signup,
})

export const EntityFederation = lazyPage(
  () => import('./features/entity-federation/entity-federation.lazy'),
  'EntityFederation',
)

export const entityFederationRoute = createRoute({
  getParentRoute: () => userManagementScopeRoute,
  path: 'federation',
  component: EntityFederation,
})

export const OAuthConsent = lazyPage(() => import('./features/oauth-consent/oauth-consent.lazy'), 'OAuthConsent')

// A client sends the browser here through /oauth/authorize. Full-bleed like sign-in.
export const oauthConsentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/oauth/consent',
  beforeLoad: ({ context, location }) => {
    if (!context.isAuthenticated) throw redirect({ to: '/auth/login', search: { redirect: location.href } })
  },
  component: OAuthConsent,
})

export const ConnectedApps = lazyPage(() => import('./features/connected-apps/connected-apps.lazy'), 'ConnectedApps')

export const connectedAppsRoute = createRoute({
  getParentRoute: () => appRoute,
  path: '/me/connected-apps',
  staticData: { title: 'Connected apps' },
  component: ConnectedApps,
})
